import { Link } from '@tanstack/react-router';
import { ChevronDownIcon } from 'lucide-react';
import { Fragment, memo } from 'react';
import { useGetProjectRequestsStats } from '@/api/projects/queries';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMediaQuery } from '@/hooks';
import { ALL_STATUSES_OPTION, STATUSES } from '../-utils/config';

const LINKS = [ALL_STATUSES_OPTION, ...Object.values(STATUSES)];

type Props = {
  projectId: string;
  status: string;
};

export const Categories = memo(({ projectId, status }: Props) => {
  const { data } = useGetProjectRequestsStats(projectId);
  const isDesktop = useMediaQuery('(min-width: 640px)');

  const activeCategory = LINKS.find((link) => link.status === status);

  if (!isDesktop) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button className="w-[200px] items-center justify-start gap-2 p-2" variant="outline">
            {activeCategory && (
              <>
                {activeCategory.Icon && (
                  <activeCategory.Icon className={activeCategory.iconClassName} size={18} />
                )}
                <p>{activeCategory.label}</p>
                <span className="text-sm text-muted-foreground">
                  {data?.[activeCategory.status === 'all' ? 'total' : activeCategory.status]}
                </span>
              </>
            )}
            <ChevronDownIcon className="ml-auto text-muted-foreground" size={18} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {LINKS.map(({ label, status: linkStatus, Icon, iconClassName }) => {
            return (
              <Fragment key={status}>
                {linkStatus === 'pending' && (
                  <div className="ml-2 pt-3">
                    <span className="text-xs text-muted-foreground">Internal statuses:</span>
                  </div>
                )}
                <DropdownMenuItem asChild key={status}>
                  <Link
                    className="bold flex items-baseline px-2 py-2 transition-colors hover:text"
                    to="/projects/$projectId/requests"
                    search={(prev) => ({ ...prev, status: linkStatus })}
                    params={{ projectId }}
                    activeProps={{
                      className: 'bg-muted',
                    }}
                    inactiveProps={{
                      className: 'text-muted-foreground',
                    }}
                  >
                    {Icon && <Icon className={`${iconClassName} self-center`} size={18} />}
                    <p>{label}</p>
                    <p className="text-sm text-muted-foreground">
                      {data?.[linkStatus === 'all' ? 'total' : linkStatus]}
                    </p>
                  </Link>
                </DropdownMenuItem>
                {linkStatus === 'rejected' && (
                  <div className="ml-2 pt-3">
                    <span className="text-xs text-muted-foreground">Visible for all users:</span>
                  </div>
                )}
              </Fragment>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="sticky top-8 flex w-full flex-col">
      <div>
        {LINKS.map(({ label, status: linkStatus, Icon, iconClassName }) => {
          return (
            <Fragment key={linkStatus}>
              {linkStatus === 'pending' && (
                <div className="ml-2 pt-3">
                  <span className="text-xs text-muted-foreground">Internal statuses:</span>
                </div>
              )}
              <Link
                className="bold flex rounded-lg items-baseline px-2 py-2.5 text-sm transition-colors hover:text-slate-900 dark:hover:text-slate-100"
                to="/projects/$projectId/requests"
                search={(prev) => ({ ...prev, status: linkStatus })}
                params={{ projectId }}
                activeProps={{
                  className: 'bg-accent',
                }}
                inactiveProps={{
                  className: 'text-muted-foreground hover:bg-accent/30',
                }}
              >
                {Icon && <Icon className={`${iconClassName} mr-2.5 self-center`} size={18} />}
                <p>{label}</p>
                <p className="ml-2 text-sm text-muted-foreground">
                  {data?.[linkStatus === 'all' ? 'total' : linkStatus]}
                </p>
              </Link>
              {linkStatus === 'rejected' && (
                <div className="ml-2 pt-3">
                  <span className="text-xs text-muted-foreground text-center">
                    Visible for all users:
                  </span>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
});
